import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation } from "swiper/modules";
import Skeleton from "@mui/material/Skeleton";
import { Box, Stack } from "@mui/material";
import { styled } from '@mui/material/styles';

const StyledSwiper = styled(Swiper)`
  &.custom-swiper {
    position: relative;
    padding: 0 30px;
    margin: 0 auto;
    overflow: visible;
  }

  .swiper-slide {
    height: auto;
    display: flex;
    justify-content: center;
  }

  .swiper-wrapper {
    align-items: center;
    display: flex;
  }

  .swiper-button-prev,
  .swiper-button-next {
    &:after {
      font-family: swiper-icons;
      font-size: 24px;
      text-transform: none !important;
      letter-spacing: 0;
      font-variant: initial;
      line-height: 1;
    }
  }

  .swiper-button-prev:after {
    content: 'prev';
  }

  .swiper-button-next:after {
    content: 'next';
  }
`;

const StyledDot = styled(Box)(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
  opacity: active === 'true' ? 1 : 0.5,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  }
}));

const CustomPagination = ({ totalSlides, activeIndex, onDotClick }) => {
  return (
    <Stack 
      direction="row" 
      spacing={1} 
      sx={{ 
        position: 'absolute',
        bottom: 5,
        left: '50%',
        transform: 'translateX(-50%)',
        display: { xs: 'flex', md: 'none' },
        zIndex: 2,
      }}
    >
      {Array.from({ length: totalSlides }).map((_, index) => (
        <StyledDot
          key={index}
          active={(index === activeIndex).toString()}
          onClick={() => onDotClick(index)}
        />
      ))}
    </Stack>
  );
};

const LoadingSkeleton = ({ count = 6 }) => (
  <StyledSwiper
    modules={[Navigation]}
    loop={false}
    className="custom-swiper"
    spaceBetween={24}
    slidesPerView={1}
    slidesPerGroup={1}
    centeredSlides={false}
    navigation={{
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    }}
    breakpoints={{
      900: {
        slidesPerView: 2,
        spaceBetween: 24,
        slidesPerGroup: 2,
      }
    }}
  >
    {Array.from({ length: count }).map((_, index) => (
      <SwiperSlide key={index}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          animation="wave"
          sx={{
            borderRadius: 1,
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.04), transparent)',
            }
          }}
        />
      </SwiperSlide>
    ))}
  </StyledSwiper>
);

export default function CustomSlider({ 
  items = [], 
  renderItem,
  loading = false,
  skeletonCount = 6,
  className = "custom-swiper",
  breakpoints = {
    900: {
      slidesPerView: 2,
      spaceBetween: 24,
      slidesPerGroup: 2,
    }
  }
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  if (loading) {
    return <LoadingSkeleton count={skeletonCount} />;
  }

  return (
    <Box sx={{ 
      position: 'relative',
      pb: { xs: 5, md: 0 },
      mx: { xs: 2, md: 4 },
      px: { xs: 1, md: 2 },
      overflow: 'hidden'
    }}>
      <StyledSwiper
        ref={swiperRef}
        modules={[Navigation]}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className={className}
        spaceBetween={24}
        slidesPerView={1}
        slidesPerGroup={1}
        centeredSlides={false}
        loop={false}
        touchRatio={1.5}
        grabCursor={true}
        resistance={true}
        resistanceRatio={0.85}
        breakpoints={breakpoints}
      >
        {items.map((item, index) => (
          <SwiperSlide 
            key={index}
          >
            {renderItem(item)}
          </SwiperSlide>
        ))}
      </StyledSwiper>

      <CustomPagination
        totalSlides={items.length}
        activeIndex={activeIndex}
        onDotClick={(index) => {
          if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.slideToLoop(index);
          }
        }}
      />

      <Box
        className="swiper-button-prev"
        sx={{
          position: 'absolute',
          left: { xs: '5px', md: '5px' },
          top: 'calc(50% - -14px)',
          transform: 'translateY(-50%)',
          zIndex: 10,
          cursor: 'pointer',
          color: 'secondary.main',
          transition: 'all 0.3s ease',
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.swiper-button-disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
          },
          '&::after': {
            fontSize: { xs: '20px', md: '24px' },
          },
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '8px',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      />
      <Box
        className="swiper-button-next"
        sx={{
          position: 'absolute',
          right: { xs: '5px', md: '5px' },
          top: 'calc(50% - -14px)',
          transform: 'translateY(-50%)',
          zIndex: 10,
          cursor: 'pointer',
          color: 'secondary.main',
          transition: 'all 0.3s ease',
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.swiper-button-disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
          },
          '&::after': {
            fontSize: { xs: '20px', md: '24px' },
          },
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '8px',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      />
    </Box>
  );
}
